<!--
 Bank Account Fix Modal - Enhanced modal for fixing NACHA and SEPA validation issues

 2025-09-18: Enhanced to support both NACHA (US) and SEPA (EU) validation types
 2025-09-18: Created modal for fixing bank account validation issues directly from export errors
 Features: Bank account selection, validation, and real-time error checking for both formats
-->

<template>
  <v-card class="bank-account-fix-modal" elevation="12">
    <v-card-title class="text-h5 align-center modal-header white--text">
      <v-icon class="pr-3" color="white" size="28">mdi-bank-check</v-icon>
      Fix {{ validationType }} Bank Account Issues
      <v-spacer />
      <v-btn icon color="white" @click="onClose">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pt-4">
      <!-- Property Info Card -->
      <v-card
        outlined
        class="property-info-card mb-4"
        color="blue-grey lighten-5"
      >
        <v-card-text class="py-3">
          <v-row align="center" no-gutters>
            <v-col cols="auto">
              <v-avatar size="40" color="primary" class="mr-3">
                <v-icon color="white">mdi-home-variant</v-icon>
              </v-avatar>
            </v-col>
            <v-col>
              <div class="text-h6 font-weight-medium primary--text">
                {{ listing.nickname || `Listing ${listing.id}` }}
                <v-chip small :color="validationTypeInfo.color" class="ml-2">
                  <v-icon small left>{{ validationTypeInfo.icon }}</v-icon>
                  {{ validationType }} ({{ validationTypeInfo.region }})
                </v-chip>
              </div>
              <div class="text-body-2 text--secondary">
                <v-icon small class="mr-1">mdi-map-marker</v-icon>
                {{ listing.address }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Current Issues -->
      <v-card v-if="currentErrors.length" outlined class="mb-4 issues-card">
        <v-card-title class="error--text pb-2">
          <v-icon class="mr-2" color="error">mdi-alert-circle-outline</v-icon>
          Issues to Resolve ({{ currentErrors.length }})
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <div class="issues-list">
            <v-chip
              v-for="(error, index) in currentErrors"
              :key="index"
              class="ma-1"
              color="error"
              text-color="white"
              small
            >
              <v-icon small left>mdi-alert</v-icon>
              {{ error }}
            </v-chip>
          </div>
        </v-card-text>
      </v-card>

      <v-form ref="form" v-model="formValid" lazy-validation>
        <v-container class="px-0">
          <!-- Listing Bank Account -->
          <v-card outlined class="mb-4 account-section">
            <v-card-title class="pb-2 account-section-header">
              <v-icon class="mr-2" color="primary" size="24">mdi-bank</v-icon>
              <span class="text-h6">Listing Trust Account</span>
              <v-spacer />
              <v-chip
                small
                :color="getAccountStatusColor('listing')"
                :text-color="
                  getAccountStatusColor('listing') === 'success'
                    ? 'white'
                    : 'black'
                "
              >
                {{ getAccountStatus('listing') }}
              </v-chip>
            </v-card-title>
            <v-divider />
            <v-card-text class="pt-4">
              <v-row>
                <v-col cols="12" md="6">
                  <bank-account-picker
                    v-model="formData.bank_account_id"
                    label="Select Listing Bank Account"
                    :clearable="true"
                    :outlined="true"
                    :dense="true"
                    :error-messages="getFieldErrors('listing_bank_account')"
                    @input="onListingAccountChange"
                  />
                </v-col>
                <v-col v-if="formData.bank_account_id" cols="12" md="6">
                  <v-text-field
                    v-model="listingAccountDetails.account_number"
                    :label="validationTypeInfo.accountNumberLabel"
                    outlined
                    dense
                    :error-messages="getFieldErrors('listing_account_number')"
                    prepend-inner-icon="mdi-numeric"
                    @input="validateField('listing_bank_account')"
                  />
                </v-col>
              </v-row>

              <!-- Account Details Card -->
              <v-card
                v-if="
                  selectedListingAccount || listingAccountDetails.account_number
                "
                outlined
                class="mt-3 account-details-card"
                color="grey lighten-5"
              >
                <v-card-text class="py-3">
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar size="36" color="primary" class="mr-3">
                        <v-icon small color="white">mdi-bank</v-icon>
                      </v-avatar>
                    </v-col>
                    <v-col>
                      <div class="text-body-2 font-weight-medium">
                        {{
                          selectedListingAccount?.account_name ||
                          'New Bank Account'
                        }}
                      </div>
                      <div class="text-caption text--secondary">
                        <span v-if="listingAccountDetails.account_number">
                          Account:
                          {{
                            formatAccountNumber(
                              listingAccountDetails.account_number
                            )
                          }}
                        </span>
                        <span
                          v-if="selectedListingAccount?.source"
                          class="ml-2"
                        >
                          | Source: {{ selectedListingAccount.source }}
                        </span>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-card-text>
          </v-card>

          <!-- Owner Bank Account -->
          <v-card outlined class="mb-4 account-section">
            <v-card-title class="pb-2 account-section-header">
              <v-icon class="mr-2" color="success" size="24"
                >mdi-account</v-icon
              >
              <span class="text-h6">Owner Bank Account</span>
              <v-spacer />
              <v-chip
                small
                :color="getAccountStatusColor('owner')"
                :text-color="
                  getAccountStatusColor('owner') === 'success'
                    ? 'white'
                    : 'black'
                "
              >
                {{ getAccountStatus('owner') }}
              </v-chip>
            </v-card-title>
            <v-divider />
            <v-card-text class="pt-4">
              <v-row>
                <v-col cols="12" md="4">
                  <bank-account-picker
                    v-model="formData.owner_bank_account_id"
                    label="Select Owner Bank Account"
                    :clearable="true"
                    :outlined="true"
                    :dense="true"
                    :error-messages="getFieldErrors('owner_bank_account')"
                    @input="onOwnerAccountChange"
                  />
                </v-col>
                <v-col
                  v-if="formData.owner_bank_account_id || allowManualEntry"
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model="ownerAccountDetails.account_number"
                    :label="validationTypeInfo.accountNumberLabel"
                    outlined
                    dense
                    :error-messages="getFieldErrors('owner_account_number')"
                    prepend-inner-icon="mdi-numeric"
                    @input="validateField('owner_bank_account')"
                  />
                </v-col>
                <v-col
                  v-if="
                    (formData.owner_bank_account_id || allowManualEntry) &&
                    validationType === 'NACHA'
                  "
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model="ownerAccountDetails.routing_number"
                    label="Routing Number"
                    outlined
                    dense
                    :error-messages="getFieldErrors('owner_routing_number')"
                    :rules="[routingNumberRule]"
                    prepend-inner-icon="mdi-bank-transfer"
                    counter="9"
                    @input="validateField('owner_bank_account')"
                  />
                </v-col>
              </v-row>

              <!-- Owner Account Name and Manual Entry Toggle -->
              <v-row>
                <v-col
                  v-if="formData.owner_bank_account_id || allowManualEntry"
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="ownerAccountDetails.account_name"
                    label="Account Name"
                    outlined
                    dense
                    :error-messages="getFieldErrors('owner_account_name')"
                    prepend-inner-icon="mdi-account"
                    @input="validateField('owner_bank_account')"
                  />
                </v-col>
                <v-col
                  :cols="
                    formData.owner_bank_account_id || allowManualEntry
                      ? '12'
                      : '12'
                  "
                  :md="
                    formData.owner_bank_account_id || allowManualEntry
                      ? '6'
                      : '8'
                  "
                  class="d-flex align-center"
                >
                  <v-card
                    outlined
                    class="pa-2 flex-grow-1"
                    color="blue-grey lighten-5"
                  >
                    <v-switch
                      v-model="allowManualEntry"
                      label="Allow manual entry"
                      color="primary"
                      dense
                      hide-details
                      inset
                    />
                    <div class="text-caption text--secondary ml-8">
                      Enable to enter details manually when no account is
                      selected
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Account Details Card -->
              <v-card
                v-if="
                  selectedOwnerAccount || ownerAccountDetails.account_number
                "
                outlined
                class="mt-3 account-details-card"
                color="grey lighten-5"
              >
                <v-card-text class="py-3">
                  <v-row align="center" no-gutters>
                    <v-col cols="auto">
                      <v-avatar size="36" color="success" class="mr-3">
                        <v-icon small color="white">mdi-account</v-icon>
                      </v-avatar>
                    </v-col>
                    <v-col>
                      <div class="text-body-2 font-weight-medium">
                        {{
                          ownerAccountDetails.account_name ||
                          selectedOwnerAccount?.account_name ||
                          'Owner Account'
                        }}
                      </div>
                      <div class="text-caption text--secondary">
                        <span v-if="ownerAccountDetails.account_number">
                          Account:
                          {{
                            formatAccountNumber(
                              ownerAccountDetails.account_number
                            )
                          }}
                        </span>
                        <span
                          v-if="ownerAccountDetails.routing_number"
                          class="ml-2"
                        >
                          | Routing: {{ ownerAccountDetails.routing_number }}
                        </span>
                        <span v-if="selectedOwnerAccount?.source" class="ml-2">
                          | Source: {{ selectedOwnerAccount.source }}
                        </span>
                      </div>
                    </v-col>
                    <v-col cols="auto">
                      <v-chip
                        small
                        :color="getAccountStatusColor('owner')"
                        :text-color="
                          getAccountStatusColor('owner') === 'success'
                            ? 'white'
                            : 'black'
                        "
                      >
                        {{ getAccountStatus('owner') }}
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-card-text>
          </v-card>

          <!-- Referrer Bank Account (if applicable) -->
          <template
            v-if="listing.referrer_percent && listing.referrer_percent > 0"
          >
            <v-card outlined class="mb-4 account-section">
              <v-card-title class="pb-2 account-section-header">
                <v-icon class="mr-2" color="warning" size="24"
                  >mdi-account-cash</v-icon
                >
                <span class="text-h6">Referrer Bank Account</span>
                <v-chip small color="warning" class="ml-2">
                  {{ listing.referrer_percent }}% referral
                </v-chip>
                <v-spacer />
                <v-chip
                  small
                  :color="getAccountStatusColor('referrer')"
                  :text-color="
                    getAccountStatusColor('referrer') === 'success'
                      ? 'white'
                      : 'black'
                  "
                >
                  {{ getAccountStatus('referrer') }}
                </v-chip>
              </v-card-title>
              <v-divider />
              <v-card-text class="pt-4">
                <v-row>
                  <v-col cols="12" md="4">
                    <bank-account-picker
                      v-model="formData.referrer_bank_account_id"
                      label="Select Referrer Bank Account"
                      :clearable="true"
                      :outlined="true"
                      :dense="true"
                      :error-messages="getFieldErrors('referrer_bank_account')"
                      @input="onReferrerAccountChange"
                    />
                  </v-col>
                  <v-col
                    v-if="formData.referrer_bank_account_id || allowManualEntry"
                    cols="12"
                    md="4"
                  >
                    <v-text-field
                      v-model="referrerAccountDetails.account_number"
                      :label="validationTypeInfo.accountNumberLabel"
                      outlined
                      dense
                      :error-messages="
                        getFieldErrors('referrer_account_number')
                      "
                      prepend-inner-icon="mdi-numeric"
                      @input="validateField('referrer_bank_account')"
                    />
                  </v-col>
                  <v-col
                    v-if="
                      (formData.referrer_bank_account_id || allowManualEntry) &&
                      validationType === 'NACHA'
                    "
                    cols="12"
                    md="4"
                  >
                    <v-text-field
                      v-model="referrerAccountDetails.routing_number"
                      label="Routing Number"
                      outlined
                      dense
                      :error-messages="
                        getFieldErrors('referrer_routing_number')
                      "
                      :rules="[routingNumberRule]"
                      prepend-inner-icon="mdi-bank-transfer"
                      counter="9"
                      @input="validateField('referrer_bank_account')"
                    />
                  </v-col>
                </v-row>

                <!-- Referrer Account Name -->
                <v-row
                  v-if="formData.referrer_bank_account_id || allowManualEntry"
                >
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="referrerAccountDetails.account_name"
                      label="Account Name"
                      outlined
                      dense
                      :error-messages="getFieldErrors('referrer_account_name')"
                      prepend-inner-icon="mdi-account"
                      @input="validateField('referrer_bank_account')"
                    />
                  </v-col>
                </v-row>

                <!-- Account Details Card -->
                <v-card
                  v-if="
                    selectedReferrerAccount ||
                    referrerAccountDetails.account_number
                  "
                  outlined
                  class="mt-3 account-details-card"
                  color="grey lighten-5"
                >
                  <v-card-text class="py-3">
                    <v-row align="center" no-gutters>
                      <v-col cols="auto">
                        <v-avatar size="36" color="warning" class="mr-3">
                          <v-icon small color="white">mdi-account-cash</v-icon>
                        </v-avatar>
                      </v-col>
                      <v-col>
                        <div class="text-body-2 font-weight-medium">
                          {{
                            referrerAccountDetails.account_name ||
                            selectedReferrerAccount?.account_name ||
                            'Referrer Account'
                          }}
                        </div>
                        <div class="text-caption text--secondary">
                          <span v-if="referrerAccountDetails.account_number">
                            Account:
                            {{
                              formatAccountNumber(
                                referrerAccountDetails.account_number
                              )
                            }}
                          </span>
                          <span
                            v-if="referrerAccountDetails.routing_number"
                            class="ml-2"
                          >
                            | Routing:
                            {{ referrerAccountDetails.routing_number }}
                          </span>
                          <span
                            v-if="selectedReferrerAccount?.source"
                            class="ml-2"
                          >
                            | Source: {{ selectedReferrerAccount.source }}
                          </span>
                        </div>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-card>
          </template>

          <!-- Validation Status -->
          <v-row v-if="hasValidated">
            <v-col cols="12">
              <v-alert
                :type="validationErrors.length === 0 ? 'success' : 'warning'"
                outlined
                class="mt-4"
              >
                <div v-if="validationErrors.length === 0">
                  <v-icon class="mr-2">mdi-check-circle</v-icon>
                  All validation checks passed! Ready to save.
                </div>
                <div v-else>
                  <v-icon class="mr-2">mdi-alert-circle</v-icon>
                  {{ validationErrors.length }} issue(s) remaining:
                  <ul class="mt-2 pl-4">
                    <li
                      v-for="error in validationErrors"
                      :key="error"
                      class="text-caption"
                    >
                      {{ error }}
                    </li>
                  </ul>
                </div>
              </v-alert>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-card-text>

    <v-divider />

    <v-card-actions class="pa-6 action-buttons">
      <v-btn large outlined :disabled="saving" class="px-8" @click="onClose">
        <v-icon left>mdi-close</v-icon>
        Cancel
      </v-btn>

      <v-spacer />

      <v-btn
        large
        color="info"
        :loading="validating"
        :disabled="saving"
        class="px-8 mr-3"
        @click="validateAccounts"
      >
        <v-icon left>mdi-check-circle-outline</v-icon>
        Validate
      </v-btn>

      <v-btn
        large
        color="primary"
        :loading="saving"
        :disabled="!hasValidated || validationErrors.length > 0"
        class="px-8"
        elevation="2"
        @click="saveChanges"
      >
        <v-icon left>mdi-content-save</v-icon>
        Save Changes
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import axios from 'axios'
import BankAccountPicker from 'components/accounting/bank-account-picker'
import { mapState } from 'vuex'
import Toaster from '@/utils/toaster'

export default {
  name: 'BankAccountFixModal',
  components: { BankAccountPicker },
  props: {
    listing: {
      type: Object,
      required: true,
    },
    initialErrors: {
      type: Array,
      default: () => [],
    },
    validationType: {
      type: String,
      default: 'NACHA',
      validator: value => ['NACHA', 'SEPA'].includes(value),
    },
    onSave: {
      type: Function,
      required: true,
    },
    onClose: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      formValid: false,
      saving: false,
      validating: false,
      hasValidated: false,
      allowManualEntry: false,
      formData: {
        bank_account_id: this.listing.bank_account_id || null,
        owner_bank_account_id: this.listing.owner_bank_account_id || null,
        referrer_bank_account_id: this.listing.referrer_bank_account_id || null,
      },
      listingAccountDetails: {
        account_number: this.listing.bank_account?.account_number || '',
      },
      ownerAccountDetails: {
        account_name: this.listing.owner_bank_account?.account_name || '',
        account_number: this.listing.owner_bank_account?.account_number || '',
        routing_number: this.listing.owner_bank_account?.routing_number || '',
      },
      referrerAccountDetails: {
        account_name: this.listing.referrer_bank_account?.account_name || '',
        account_number:
          this.listing.referrer_bank_account?.account_number || '',
        routing_number:
          this.listing.referrer_bank_account?.routing_number || '',
      },
      currentErrors: [...this.initialErrors],
      validationErrors: [...this.initialErrors],
      fieldErrors: {},
    }
  },
  computed: {
    ...mapState('bankAccounts', {
      allBankAccounts: 'allAccounts',
    }),
    selectedListingAccount() {
      return this.allBankAccounts.find(
        acc => acc.id === this.formData.bank_account_id
      )
    },
    selectedOwnerAccount() {
      return this.allBankAccounts.find(
        acc => acc.id === this.formData.owner_bank_account_id
      )
    },
    selectedReferrerAccount() {
      return this.allBankAccounts.find(
        acc => acc.id === this.formData.referrer_bank_account_id
      )
    },
    validationTypeInfo() {
      return {
        NACHA: {
          region: 'US',
          accountNumberLabel: 'Account Number',
          requiresRouting: true,
          icon: 'mdi-bank-transfer',
          color: 'primary',
        },
        SEPA: {
          region: 'EU',
          accountNumberLabel: 'IBAN',
          requiresRouting: false,
          icon: 'mdi-currency-eur',
          color: 'success',
        },
      }[this.validationType]
    },
  },
  methods: {
    getFieldErrors(fieldType) {
      return this.fieldErrors[fieldType] || []
    },
    formatAccountNumber(accountNumber) {
      if (!accountNumber) return ''
      // Show last 4 digits with asterisks
      if (accountNumber.length > 4) {
        return '****' + accountNumber.slice(-4)
      }
      return accountNumber
    },
    routingNumberRule(value) {
      if (this.validationType === 'SEPA') {
        // SEPA doesn't use routing numbers, IBAN contains all routing info
        return true
      }
      if (!value) return 'Routing number is required'
      if (!/^\d{9}$/.test(value))
        return 'Routing number must be exactly 9 digits'
      return true
    },
    getAccountStatus(accountType) {
      const errors = this.getFieldErrors(`${accountType}_bank_account`)
      if (errors.length === 0 && this.hasValidated) return 'Valid'
      if (errors.length > 0) return 'Invalid'
      return 'Pending'
    },
    getAccountStatusColor(accountType) {
      const status = this.getAccountStatus(accountType)
      switch (status) {
        case 'Valid':
          return 'success'
        case 'Invalid':
          return 'error'
        default:
          return 'grey'
      }
    },
    onListingAccountChange(accountId) {
      this.validateField('listing_bank_account')

      if (accountId && this.selectedListingAccount) {
        // Auto-populate account details from selected account
        this.listingAccountDetails.account_number =
          this.selectedListingAccount.account_number || ''
      } else {
        // Clear details when no account selected
        this.listingAccountDetails.account_number = ''
      }
    },
    onOwnerAccountChange(accountId) {
      this.validateField('owner_bank_account')

      if (accountId && this.selectedOwnerAccount) {
        // Auto-populate account details from selected account
        this.ownerAccountDetails = {
          account_name: this.selectedOwnerAccount.account_name || '',
          account_number: this.selectedOwnerAccount.account_number || '',
          routing_number: this.selectedOwnerAccount.routing_number || '',
        }
      } else {
        // Clear details when no account selected
        this.ownerAccountDetails = {
          account_name: '',
          account_number: '',
          routing_number: '',
        }
      }
    },
    onReferrerAccountChange(accountId) {
      this.validateField('referrer_bank_account')

      if (accountId && this.selectedReferrerAccount) {
        // Auto-populate account details from selected account
        this.referrerAccountDetails = {
          account_name: this.selectedReferrerAccount.account_name || '',
          account_number: this.selectedReferrerAccount.account_number || '',
          routing_number: this.selectedReferrerAccount.routing_number || '',
        }
      } else {
        // Clear details when no account selected
        this.referrerAccountDetails = {
          account_name: '',
          account_number: '',
          routing_number: '',
        }
      }
    },
    validateField(fieldType) {
      // Clear field-specific errors when user makes changes
      if (this.fieldErrors[fieldType]) {
        this.$set(this.fieldErrors, fieldType, [])
      }

      // Reset validation status when user makes changes
      if (this.hasValidated) {
        this.hasValidated = false
      }
    },
    async validateAccounts() {
      this.validating = true
      this.fieldErrors = {}

      try {
        // Create a mock listing object with the form data for validation
        const testListing = {
          ...this.listing,
          bank_account: this.selectedListingAccount
            ? {
                ...this.selectedListingAccount,
                account_number: this.listingAccountDetails.account_number,
              }
            : null,
          owner_bank_account:
            this.selectedOwnerAccount || this.allowManualEntry
              ? {
                  ...(this.selectedOwnerAccount || {}),
                  account_name: this.ownerAccountDetails.account_name,
                  account_number: this.ownerAccountDetails.account_number,
                  routing_number: this.ownerAccountDetails.routing_number,
                }
              : null,
          referrer_bank_account:
            this.selectedReferrerAccount ||
            (this.allowManualEntry && this.listing.referrer_percent)
              ? {
                  ...(this.selectedReferrerAccount || {}),
                  account_name: this.referrerAccountDetails.account_name,
                  account_number: this.referrerAccountDetails.account_number,
                  routing_number: this.referrerAccountDetails.routing_number,
                }
              : null,
        }

        // Use the appropriate validation logic based on type
        const errors =
          this.validationType === 'SEPA'
            ? this.validateListingForSepa(testListing)
            : this.validateListingForNacha(testListing)

        this.validationErrors = errors
        this.hasValidated = true

        // Categorize errors by field type for better UX
        this.categorizeErrors(errors)
      } catch (error) {
        console.error('Validation error:', error)
        Toaster.show([
          {
            type: 'error',
            text: 'Failed to validate accounts. Please try again.',
          },
        ])
      } finally {
        this.validating = false
      }
    },
    categorizeErrors(errors) {
      const fieldErrors = {
        listing_bank_account: [],
        owner_bank_account: [],
        referrer_bank_account: [],
      }

      errors.forEach(error => {
        if (error.includes('listing bank account')) {
          fieldErrors.listing_bank_account.push(error)
        } else if (error.includes('owner bank account')) {
          fieldErrors.owner_bank_account.push(error)
        } else if (error.includes('referrer bank account')) {
          fieldErrors.referrer_bank_account.push(error)
        }
      })

      this.fieldErrors = fieldErrors
    },
    validateListingForNacha(listing) {
      const errors = []

      // Validate listing bank account
      if (!listing.bank_account) {
        errors.push('Missing listing bank account info')
      } else if (!listing.bank_account.account_number) {
        errors.push('Missing listing bank account number')
      }

      // Validate owner bank account
      if (!listing.owner_bank_account) {
        errors.push('Missing listing owner bank account info')
      } else {
        if (!listing.owner_bank_account.account_name) {
          errors.push('Missing listing owner bank account name')
        }
        if (!listing.owner_bank_account.account_number) {
          errors.push('Missing listing owner bank account number')
        }
        if (!listing.owner_bank_account.routing_number) {
          errors.push('Missing listing owner bank account routing number')
        } else if (
          !listing.owner_bank_account.routing_number.match(/^\d{9}$/)
        ) {
          errors.push('Invalid routing number (must be 9 digits)')
        }
      }

      // Validate referrer bank account if applicable
      if (listing.referrer_percent && listing.referrer_percent > 0) {
        if (!listing.referrer_bank_account) {
          errors.push('Missing listing referrer bank account info')
        } else {
          if (!listing.referrer_bank_account.account_name) {
            errors.push('Missing listing referrer bank account name')
          }
          if (!listing.referrer_bank_account.account_number) {
            errors.push('Missing listing referrer bank account number')
          }
          if (!listing.referrer_bank_account.routing_number) {
            errors.push('Missing listing referrer bank account routing number')
          } else if (
            !listing.referrer_bank_account.routing_number.match(/^\d{9}$/)
          ) {
            errors.push('Referrer: Invalid routing number (must be 9 digits)')
          }
        }
      }

      return errors
    },
    validateListingForSepa(listing) {
      const errors = []

      // Validate listing bank account (debtor)
      if (!listing.bank_account) {
        errors.push('Missing listing bank account info')
      } else if (!listing.bank_account.account_number) {
        errors.push('Missing listing bank account IBAN')
      } else if (!this.validateIban(listing.bank_account.account_number)) {
        errors.push('Invalid listing bank account IBAN format')
      }

      // Validate owner bank account (creditor)
      if (!listing.owner_bank_account) {
        errors.push('Missing listing owner bank account info')
      } else {
        if (!listing.owner_bank_account.account_name) {
          errors.push('Missing listing owner bank account name')
        }
        if (!listing.owner_bank_account.account_number) {
          errors.push('Missing listing owner bank account IBAN')
        } else if (
          !this.validateIban(listing.owner_bank_account.account_number)
        ) {
          errors.push('Invalid owner bank account IBAN format')
        }
      }

      // Validate referrer bank account if applicable
      if (listing.referrer_percent && listing.referrer_percent > 0) {
        if (!listing.referrer_bank_account) {
          errors.push('Missing listing referrer bank account info')
        } else {
          if (!listing.referrer_bank_account.account_name) {
            errors.push('Missing listing referrer bank account name')
          }
          if (!listing.referrer_bank_account.account_number) {
            errors.push('Missing listing referrer bank account IBAN')
          } else if (
            !this.validateIban(listing.referrer_bank_account.account_number)
          ) {
            errors.push('Invalid referrer bank account IBAN format')
          }
        }
      }

      return errors
    },
    validateIban(iban) {
      if (!iban) return false

      // Remove spaces and convert to uppercase
      iban = iban.replace(/\s/g, '').toUpperCase()

      // Basic length check (IBAN should be between 15 and 34 characters)
      if (iban.length < 15 || iban.length > 34) return false

      // Check if it starts with 2 letters followed by 2 digits
      if (!/^[A-Z]{2}\d{2}/.test(iban)) return false

      // Move first 4 characters to end and convert letters to numbers
      const rearranged = iban.substring(4) + iban.substring(0, 4)
      let numeric = ''

      for (let i = 0; i < rearranged.length; i++) {
        const char = rearranged[i]
        if (/[A-Z]/.test(char)) {
          numeric += (char.charCodeAt(0) - 'A'.charCodeAt(0) + 10).toString()
        } else {
          numeric += char
        }
      }

      // Validate using mod 97
      let remainder = numeric.substring(0, 9) % 97
      for (let i = 9; i < numeric.length; i += 7) {
        remainder = (remainder + numeric.substring(i, i + 7)) % 97
      }

      return remainder === 1
    },
    async saveChanges() {
      if (!this.hasValidated || this.validationErrors.length > 0) {
        return
      }

      this.saving = true

      try {
        // Prepare payload with account details
        const payload = {
          ...this.formData,
          listing_account_details: this.listingAccountDetails,
          owner_account_details: this.ownerAccountDetails,
          referrer_account_details: this.referrerAccountDetails,
        }

        console.log('Saving bank account changes with payload:', payload)

        const response = await axios.post(
          `/api/listings/${this.listing.id}/bank-accounts`,
          payload
        )

        console.log('Bank account update response:', response.data)

        // Update the current listing data with the response
        const updatedListing = response.data

        // Update the parent component's data immediately
        this.onSave(updatedListing)

        Toaster.show([
          {
            type: 'success',
            text: 'Bank accounts updated successfully',
          },
        ])
      } catch (error) {
        console.error('Save error:', error)
        Toaster.show([
          {
            type: 'error',
            text:
              error.response?.data?.error ||
              'Failed to save bank account changes',
          },
        ])
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.bank-account-fix-modal {
  border-radius: 16px !important;
  overflow: hidden;
}

.modal-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  border-radius: 0;
  padding: 20px 24px;
}

.property-info-card {
  border-radius: 12px;
  border: 2px solid #e3f2fd;
}

.issues-card {
  border-radius: 12px;
  border: 2px solid #ffebee;
}

.issues-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.account-section {
  border-radius: 12px;
  border: 2px solid #f5f5f5;
  transition: all 0.3s ease;
}

.account-section:hover {
  border-color: #e0e0e0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.account-section-header {
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-bottom: 1px solid #e0e0e0;
}

.account-details-card {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #fafafa 0%, #f9f9f9 100%);
}

.action-buttons {
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-top: 1px solid #e0e0e0;
}

/* Form field enhancements */
.v-text-field--outlined .v-input__prepend-inner {
  margin-top: 16px;
}

/* Status chip animations */
.v-chip {
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

/* Button enhancements */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.v-btn:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

/* Card hover effects */
.v-card {
  transition: all 0.3s ease;
}

.account-section .v-card-text {
  padding: 20px !important;
}

/* Avatar styling */
.v-avatar {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Switch styling */
.v-input--switch {
  margin-top: 8px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .modal-header {
    padding: 16px 20px;
  }

  .account-section-header {
    padding: 12px 16px !important;
  }

  .action-buttons {
    padding: 16px !important;
  }

  .v-btn.px-8 {
    padding: 0 16px !important;
  }
}
</style>
